import { jsx as _jsx } from "react/jsx-runtime";
/**
 * @fileoverview SponsoredBadge — always-visible "Sponsored" disclosure pill
 *
 * FTC 16 CFR Part 255 requires paid-placement disclosure to be visible
 * without interaction. This badge is unconditionally rendered — there is no
 * hover state that hides or reveals it — and its label is fixed rather than
 * a `children` slot, so a consumer cannot omit or reword the disclosure
 * text. Pair with `<SponsoredTooltip>` for the supplementary "why am I
 * seeing this" detail (DES-49 copy).
 *
 * This badge is designed to sit in the card's top-right corner, directly
 * over the hero media (an arbitrary photo, not a controlled surface) — see
 * DES-50 "Sponsored card (all tiers)". Because the photo behind it can be
 * any brightness, every tier uses a fixed (theme-invariant) scrim rather
 * than a theme-mode-dependent semantic color: verified by rendering the
 * locked DES-50 spec (`Sponsored Tier Visual System.html`) in a real browser
 * and sampling the card badge's computed styles directly, since the
 * handoff README's abbreviated token names (`--glass-bg`, `--accent-wash`,
 * `--cta`@16%) collapse this per-tier nuance.
 *
 * Tier treatment (pixel-sampled from the rendered spec):
 * - `starter` — dark scrim (dark-theme `background`, `#0F141F`, @ 60%),
 *   white text.
 * - `growth` — CLT Skyline scrim (`brandColors.city.clt.skyline`, `#0E7B7B`,
 *   @ 28%), white text.
 * - `featured` — Electric Amber scrim (`lociAmber`, `#FFB800`, @ 16%),
 *   AA-darkened amber text (matches `theme.colors.warning` in light mode —
 *   the best-contrast choice against unpredictable media in either theme).
 *
 * Colors below are local literals rather than imports from
 * `theme/design-tokens.ts` — same convention as the existing `AmberPill`
 * atom's `AMBER_COLOR` constant — because these values are fixed regardless
 * of the active `ThemeProvider` mode, so importing the mode-switched theme
 * constants would add an indirection without a behavioral benefit.
 */
import { forwardRef } from 'react';
import { useTheme } from '../../../theme';
// Base hex values, exported so no consumer needs to re-literal these and
// risk drifting from this file — same rationale as FEATURED_AMBER below.
// Currently only FEATURED_AMBER has an external consumer (SponsoredTooltip's
// featured-tier trigger); starter/growth are exported preemptively for
// WEB-36 (the sponsored card variant composing these atoms) since that's a
// concrete, near-term consumer, not speculative generality.
export const STARTER_SCRIM = '#0F141F'; // dark-theme background
export const GROWTH_SKYLINE = '#0E7B7B'; // brandColors.city.clt.skyline
export const GROWTH_SKYLINE_GLOW = '#4FC4C4'; // brandColors.city.clt.skylineGlow
// lociAmber — shared with SponsoredTooltip's `featured`-tier trigger colors
// (the "?" affordance next to this badge) so the two atoms' amber never
// drifts apart. Exported rather than duplicated as a second local literal.
export const FEATURED_AMBER = '#FFB800';
export const FEATURED_AMBER_TEXT = '#CC9300'; // AA-darkened amber (theme.colors.warning, light)
// Hex + 2-digit alpha suffix (matches the existing `FollowButton` convention
// — `${theme.colors.accent}1A` — rather than a new rgba() helper). Every
// value below is fixed across light/dark because the badge composites over
// arbitrary hero media, not the page surface.
const TIER_COLORS = {
    starter: {
        background: `${STARTER_SCRIM}99`, // @ 60% — dark scrim
        border: '#FFFFFF24', // white @ 14%
        color: '#FFFFFF',
    },
    growth: {
        background: `${GROWTH_SKYLINE}47`, // @ 28%
        border: `${GROWTH_SKYLINE_GLOW}80`, // @ 50%
        color: '#FFFFFF',
    },
    featured: {
        background: `${FEATURED_AMBER}29`, // lociAmber @ 16%
        border: `${FEATURED_AMBER}73`, // lociAmber @ 45%
        color: FEATURED_AMBER_TEXT,
    },
};
/**
 * `<SponsoredBadge>` — the always-visible "Sponsored" pill atom.
 */
export const SponsoredBadge = forwardRef(({ tier, style, className, ...rest }, ref) => {
    const theme = useTheme();
    const tierColors = TIER_COLORS[tier];
    const badgeStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        fontFamily: theme.typography.fontFamilyMono,
        fontSize: '9px',
        fontWeight: 600,
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
        lineHeight: 1,
        padding: '3px 8px',
        borderRadius: theme.borderRadius.full,
        borderWidth: 1,
        borderStyle: 'solid',
        backgroundColor: tierColors.background,
        borderColor: tierColors.border,
        color: tierColors.color,
        whiteSpace: 'nowrap',
        ...style,
    };
    const classes = ['loci-sponsored-badge', className].filter(Boolean).join(' ');
    return (_jsx("span", { ref: ref, className: classes, style: badgeStyle, "data-testid": "sponsored-badge", "data-tier": tier, ...rest, children: "Sponsored" }));
});
SponsoredBadge.displayName = 'SponsoredBadge';
